import { ControlledInput } from "components/Card/ControlledInput";
import { MaskedInput } from "components/inputs";
import React from "react";
import type { Control, FieldErrors } from "react-hook-form";
import type { MSADecisionLineItemType } from "types/DataTypes";

import type { FormState } from "./useCMSDecisionModal";

export const getAllocationValues = (
  item: MSADecisionLineItemType,
  type: "proposed" | "decision" = "proposed",
) => {
  const prefix = type === "proposed" ? "proposed" : "decision";
  const isMedical = item?.decision_treatment_allocation?.id;

  return isMedical
    ? {
        annual_cost: item?.[`${prefix}_treatment_allocation`]?.annual_cost,
        number_of_years:
          item?.[`${prefix}_treatment_allocation`]?.number_of_years,
        name: item?.[`decision_treatment_allocation`]?.name,
        total_cost: item?.[`${prefix}_treatment_allocation`]?.total_cost,
        unit_price: item?.[`${prefix}_treatment_allocation`]?.unit_price,
        units_per_year:
          item?.[`${prefix}_treatment_allocation`]?.units_per_year,
        type: item?.[`${prefix}_treatment_allocation`]?.type,
        id: item?.[`${prefix}_treatment_allocation`]?.id,
        dosage: null,
        units_per_month: null,
      }
    : {
        annual_cost: item?.[`${prefix}_prescription_allocation`]?.annual_cost,
        number_of_years:
          item?.[`${prefix}_prescription_allocation`]?.number_of_years,
        name: item?.[`decision_prescription_allocation`]?.name,
        total_cost: item?.[`${prefix}_prescription_allocation`]?.total_cost,
        unit_price: item?.[`${prefix}_prescription_allocation`]?.unit_price,
        units_per_year:
          item?.[`${prefix}_prescription_allocation`]?.units_per_year,
        type: item?.[`${prefix}_prescription_allocation`]?.type,
        id: item?.[`${prefix}_prescription_allocation`]?.id,
        dosage: item?.[`${prefix}_prescription_allocation`]?.dosage,
        units_per_month:
          item?.[`${prefix}_prescription_allocation`]?.units_per_month,
      };
};

type DecisionContentProps = {
  name: string;
  control: Control<FormState>;
  errors: FieldErrors<FormState>;
  itemId: number;
  handleSave: (id: number, field: string, value: string | number) => void;
  savingFields: { [key: string]: boolean };
};

export const TreatmentDecisionContent: React.FC<DecisionContentProps> = ({
  name,
  control,
  errors,
  itemId,
  handleSave,
  savingFields,
}) => {
  return (
    <div className="grid-layout gl-2 cms-decision">
      <div className="data-values">
        <label className="grid-2-wide">
          <b>PROPOSED</b>
        </label>
        <label>Treatment:</label>
        <figure>{name}</figure>

        <label>Cost / Unit:</label>
        <ControlledInput
          control={control}
          name="proposed_unit_price"
          errors={errors}
          componentType={MaskedInput}
          mask="dollar"
          readOnly={true}
        />

        <label>Units / Year:</label>
        <ControlledInput
          control={control}
          name="proposed_units_per_year"
          errors={errors}
          componentType={MaskedInput}
          mask="number"
          readOnly={true}
        />

        <label>Annual Cost:</label>
        <ControlledInput
          control={control}
          name="proposed_annual_cost"
          errors={errors}
          componentType={MaskedInput}
          mask="dollar"
          readOnly={true}
        />

        <label># of Years:</label>
        <ControlledInput
          control={control}
          name="proposed_number_of_years"
          errors={errors}
          componentType={MaskedInput}
          mask="number"
          readOnly={true}
        />

        <label>Total:</label>
        <ControlledInput
          control={control}
          name="proposed_total_cost"
          errors={errors}
          componentType={MaskedInput}
          mask="dollar"
          readOnly={true}
        />
      </div>

      <div className="data-values">
        <label className="grid-2-wide">
          <b>DECISION</b>
        </label>
        <label>Treatment:</label>
        <figure>{name}</figure>

        <label>Cost / Unit:</label>
        <ControlledInput
          control={control}
          name="unit_price"
          errors={errors}
          onSave={async (value) => {
            return handleSave(Number(itemId), "unit_price", value);
          }}
          componentType={MaskedInput}
          mask="dollar"
          classNames={savingFields["unit_price"] ? "saving" : ""}
        />

        <label>Units / Year:</label>
        <ControlledInput
          control={control}
          name="units_per_year"
          mask="number"
          errors={errors}
          onSave={async (value) => {
            return handleSave(Number(itemId), "units_per_year", value);
          }}
          componentType={MaskedInput}
          maxLength={5}
          classNames={savingFields["units_per_year"] ? "saving" : ""}
        />

        <label>Annual Cost:</label>
        <ControlledInput
          control={control}
          name="annual_cost"
          errors={errors}
          componentType={MaskedInput}
          mask="dollar"
          readOnly={true}
        />

        <label># of Years:</label>
        <ControlledInput
          control={control}
          name="number_of_years"
          mask="number"
          errors={errors}
          onSave={async (value) => {
            return handleSave(Number(itemId), "number_of_years", value);
          }}
          componentType={MaskedInput}
          maxLength={5}
          classNames={savingFields["number_of_years"] ? "saving" : ""}
        />

        <label>Total:</label>
        <ControlledInput
          control={control}
          name="total_cost"
          errors={errors}
          componentType={MaskedInput}
          mask="dollar"
          readOnly={true}
        />
      </div>
    </div>
  );
};

export const PrescriptionDecisionContent: React.FC<DecisionContentProps> = ({
  name,
  control,
  errors,
  itemId,
  handleSave,
  savingFields,
}) => {
  return (
    <div className="grid-layout gl-2 cms-decision">
      <div className="data-values">
        <label className="grid-2-wide">
          <b>PROPOSED</b>
        </label>
        <label>Prescription:</label>
        <figure>{name}</figure>

        <label>Unit Price:</label>
        <ControlledInput
          control={control}
          name="proposed_unit_price"
          errors={errors}
          componentType={MaskedInput}
          mask="dollar"
          readOnly={true}
        />

        <label>Dosage:</label>
        <ControlledInput
          control={control}
          name="proposed_dosage"
          errors={errors}
          componentType={MaskedInput}
          mask="number"
          readOnly={true}
        />

        <label>Units / Month:</label>
        <ControlledInput
          control={control}
          name="proposed_units_per_month"
          errors={errors}
          componentType={MaskedInput}
          mask="number"
          readOnly={true}
        />

        <label># of Years:</label>
        <ControlledInput
          control={control}
          name="proposed_number_of_years"
          errors={errors}
          componentType={MaskedInput}
          mask="number"
          readOnly={true}
        />

        <label>Total:</label>
        <ControlledInput
          control={control}
          name="proposed_total_cost"
          errors={errors}
          componentType={MaskedInput}
          mask="dollar"
          readOnly={true}
        />
      </div>

      <div className="data-values">
        <label className="grid-2-wide">
          <b>DECISION</b>
        </label>
        <label>Prescription:</label>
        <figure>{name}</figure>

        <label>Cost / Unit:</label>
        <ControlledInput
          control={control}
          name="unit_price"
          errors={errors}
          onSave={async (value) => {
            return handleSave(Number(itemId), "unit_price", value);
          }}
          componentType={MaskedInput}
          mask="dollar"
          classNames={savingFields["unit_price"] ? "saving" : ""}
        />

        <label>Dosage:</label>
        <ControlledInput
          control={control}
          name="dosage"
          mask="number"
          errors={errors}
          onSave={async (value) => {
            return handleSave(Number(itemId), "dosage", value);
          }}
          componentType={MaskedInput}
          maxLength={5}
          classNames={savingFields["dosage"] ? "saving" : ""}
        />

        <label>Units / Month:</label>
        <ControlledInput
          control={control}
          name="units_per_month"
          mask="number"
          errors={errors}
          onSave={async (value) => {
            return handleSave(Number(itemId), "units_per_month", value);
          }}
          componentType={MaskedInput}
          maxLength={5}
          classNames={savingFields["units_per_month"] ? "saving" : ""}
        />

        <label># of Years:</label>
        <ControlledInput
          control={control}
          name="number_of_years"
          mask="number"
          errors={errors}
          onSave={async (value) => {
            return handleSave(Number(itemId), "number_of_years", value);
          }}
          componentType={MaskedInput}
          maxLength={5}
          classNames={savingFields["number_of_years"] ? "saving" : ""}
        />

        <label>Total:</label>
        <ControlledInput
          control={control}
          name="total_cost"
          errors={errors}
          componentType={MaskedInput}
          mask="dollar"
          readOnly={true}
        />
      </div>
    </div>
  );
};
